import axiosConfig from "./axiosConfig";

// Système de cache pour les requêtes GET
const cache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes en millisecondes

class StatusService {
  constructor() {
    this.statusesUrl = "/v1/statuses";
    this.articleStatusUrl = "/v1/articles";
  }

  /**
   * Vérifie si des données sont disponibles dans le cache
   * @param {string} cacheKey Clé de cache
   * @param {boolean} useCache Utiliser le cache ou non
   * @returns {Object|null} Données en cache ou null
   */
  _checkCache(cacheKey, useCache = true) {
    if (useCache && cache.has(cacheKey)) {
      const cachedData = cache.get(cacheKey);
      if (Date.now() - cachedData.timestamp < CACHE_DURATION) {
        return cachedData.data;
      }
    }
    return null;
  }

  /**
   * Met en cache des données
   * @param {string} cacheKey Clé de cache
   * @param {Object} data Données à stocker
   * @param {boolean} useCache Activer la mise en cache
   */
  _setCache(cacheKey, data, useCache = true) {
    if (useCache) {
      cache.set(cacheKey, {
        data,
        timestamp: Date.now()
      });
    }
  }

  /**
   * Récupère tous les statuts
   * @param {boolean} useCache Utiliser le cache si disponible
   * @returns {Promise<Object>} Liste des statuts
   */
  getAll(useCache = true) {
    const cacheKey = 'statuses-all';
    const cachedData = this._checkCache(cacheKey, useCache);
    
    if (cachedData) return Promise.resolve(cachedData);
    
    return axiosConfig.get(this.statusesUrl)
      .then(response => {
        const data = response.data || { data: [] };
        this._setCache(cacheKey, data, useCache);
        return data;
      })
      .catch(error => {
        console.error("Error fetching status:", error);
        return { data: [] };
      });
  }

  /**
   * Met à jour le statut d'un article
   * @param {number} id ID de l'article
   * @param {Object} data Données de statut à mettre à jour
   * @returns {Promise<Object>} Résultat de la mise à jour
   */
  updateStatus(id, data) {
    return axiosConfig.put(`${this.articleStatusUrl}/${id}/status`, data, {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    })
    .then(response => response.data)
    .catch(error => {
      console.error("Error updating status:", error);
      return Promise.reject(error.response?.data || error.message);
    });
  }

  /**
   * Met à jour plusieurs statuts d'articles en parallèle
   * @param {Array<Object>} updateDataArray Tableau d'objets {id, data}
   * @returns {Promise<Array>} Résultats des mises à jour
   */
  updateMultipleStatuses(updateDataArray) {
    const promises = updateDataArray.map(({ id, data }) => 
      this.updateStatus(id, data)
    );
    return Promise.all(promises);
  }

  /**
   * Récupère un statut par son ID
   * @param {number} id ID du statut
   * @param {boolean} useCache Utiliser le cache si disponible
   * @returns {Promise<Object>} Le statut
   */
  getStatusById(id, useCache = true) {
    const cacheKey = `status-${id}`;
    const cachedData = this._checkCache(cacheKey, useCache);
    
    if (cachedData) return Promise.resolve(cachedData);
    
    return axiosConfig.get(`${this.statusesUrl}/${id}`)
      .then(response => {
        this._setCache(cacheKey, response.data, useCache);
        return response.data;
      })
      .catch(error => {
        console.error(`Error fetching status with id ${id}:`, error);
        return Promise.reject(error.response?.data || error.message);
      });
  }

  /**
   * Invalide tout le cache des statuts
   */
  invalidateCache() {
    for (const key of cache.keys()) {
      if (key.startsWith('status')) {
        cache.delete(key);
      }
    }
  }
}

const statusServiceInstance = new StatusService();
export default statusServiceInstance;